import { DOCUMENT } from '@angular/common';
import {
  Directive,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';

@Directive({
  selector: '[appUploadMedia]',
})
export class UploadMediaDirective implements OnInit, OnChanges, OnDestroy {
  @Input() multiple: boolean;
  @Output() changeFile: EventEmitter<File[]>;
  @Input() onlyImages: boolean;
  @Input() onlyPDF: boolean;
  private inputRef: HTMLInputElement;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {
    this.inputRef = {} as HTMLInputElement;
    this.multiple = false;
    this.changeFile = new EventEmitter<File[]>();
    this.onlyImages = false;
    this.onlyPDF = false;
  }

  @HostListener('click')
  click(): void {
    this.inputRef.click();
  }

  ngOnInit(): void {
    this.injectInput();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('multiple' in changes && this.inputRef) {
      this.inputRef.multiple = this.multiple;
    }
  }

  ngOnDestroy(): void {
    if (this.inputRef) {
      this.document.body.removeChild(this.inputRef);
    }
  }

  private injectInput(): void {
    this.inputRef = this.document.createElement('input');
    this.inputRef.type = 'file';
    this.inputRef.multiple = this.multiple;
    this.inputRef.style.display = 'none';
    this.inputRef.id = UploadMediaDirective.makeId();
    this.inputRef.addEventListener('change', this.onChange.bind(this));
    this.document.body.appendChild(this.inputRef);
  }

  private static makeId(): string {
    const length = 20;
    let s = '';
    while (s.length < length) {
      s += Math.random()
        .toString(36)
        .substr(2, length - s.length);
    }
    return s;
  }

  private onChange(event: any): void {
    if (this.onlyPDF) {
      this.readOnlyPDF(event);
      return;
    }
    if (this.onlyImages) {
      this.readOnlyImages(event);
      return;
    }
    this.readImagesAndVideos(event);
  }

  private readImagesAndVideos(event: any): void {
    const filesUpload: any = [];
    const files = event.target.files as FileList;
    if (!files || files.length === 0) {
      return;
    }

    for (let i = 0; i < files.length; i++) {
      if (
        files[i].type == 'image/png' ||
        files[i].type == 'image/jpeg' ||
        files[i].type == 'video/mp4' ||
        files[i].type == 'video/avi' ||
        files[i].type == 'video/x-matroska' ||
        files[i].type == 'video/quicktime'
      ) {
        if (files[i].size / 1024 / 1024 > 50) {
          forkJoin([
            this.translate.get('error'),
            this.translate.get('maximum_allowed_40_mb'),
          ]).subscribe((translate) => {
            this.toastr.error(translate[1], translate[0].toUpperCase());
          });
        } else {
          filesUpload.push(files[i]);
        }
      } else {
        forkJoin([
          this.translate.get('error'),
          this.translate.get('jpg_png_mp4_avi_mkv_mov_files_only'),
        ]).subscribe((translate) => {
          this.toastr.error(translate[1], translate[0].toUpperCase());
        });
      }
    }

    this.changeFile.emit(filesUpload);
    this.inputRef.value = '';
  }

  private readOnlyImages(event: any): void {
    const filesUpload: any = [];
    const files = event.target.files as FileList;
    if (!files || files.length === 0) {
      return;
    }

    for (let i = 0; i < files.length; i++) {
      if (files[i].type == 'image/png' || files[i].type == 'image/jpeg') {
        if (files[i].size / 1024 / 1024 > 50) {
          forkJoin([
            this.translate.get('error'),
            this.translate.get('maximum_allowed_40_mb'),
          ]).subscribe((translate) => {
            this.toastr.error(translate[1], translate[0].toUpperCase());
          });
        } else {
          filesUpload.push(files[i]);
        }
      } else {
        forkJoin([
          this.translate.get('error'),
          this.translate.get('only_files_jpg_png'),
        ]).subscribe((translate) => {
          this.toastr.error(translate[1], translate[0].toUpperCase());
        });
      }
    }

    this.changeFile.emit(filesUpload);
    this.inputRef.value = '';
  }

  private readOnlyPDF(event: any): void {
    const filesUpload: any = [];
    const files = event.target.files as FileList;
    if (!files || files.length === 0) {
      return;
    }

    for (let i = 0; i < files.length; i++) {
      if (files[i].type == 'application/pdf') {
        if (files[i].size / 1024 / 1024 > 50) {
          forkJoin([
            this.translate.get('error'),
            this.translate.get('maximum_allowed_40_mb'),
          ]).subscribe((translate) => {
            this.toastr.error(translate[1], translate[0].toUpperCase());
          });
        } else {
          filesUpload.push(files[i]);
        }
      } else {
        forkJoin([
          this.translate.get('error'),
          this.translate.get('pdf_files_only'),
        ]).subscribe((translate) => {
          this.toastr.error(translate[1], translate[0].toUpperCase());
        });
      }
    }

    this.changeFile.emit(filesUpload);
    this.inputRef.value = '';
  }
}
